import React from 'react';
import { Search, Navigation, CheckCircle2 } from 'lucide-react';
import { Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';

const PreferencesStep = ({
    formData, updateField, toggleAvailability, genders, availability,
    isLoaded, autocompleteRef, handlePlaceSelect, handleUseCurrentLocation,
    showMap, setShowMap
}) => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Tutor Gender Preference</label>
            <div className="grid grid-cols-3 gap-3">
                {genders.map(g => (
                    <button
                        key={g.value}
                        onClick={() => updateField('gender_preference', g.value)}
                        className={`p-4 rounded-2xl border-2 text-center transition-all ${formData.gender_preference === g.value
                            ? 'border-[#041f96] bg-[#041f96]/5 shadow-md shadow-blue-100'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        {React.createElement(g.icon, { className: "h-6 w-6 block mb-1 text-[#041f96]" })}
                        <p className="font-semibold text-sm text-gray-900">{g.label}</p>
                    </button>
                ))}
            </div>
        </div>

        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">When are you available?</label>
            <div className="grid grid-cols-3 gap-3">
                {availability.map(a => (
                    <button
                        key={a.value}
                        onClick={() => toggleAvailability(a.value)}
                        className={`p-4 rounded-2xl border-2 text-center transition-all ${formData.availability.includes(a.value)
                            ? 'border-[#041f96] bg-[#041f96]/5 shadow-md shadow-blue-100'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                    >
                        {React.createElement(a.icon, { className: "h-6 w-6 block mb-1 text-[#041f96]" })}
                        <p className="font-semibold text-sm text-gray-900">{a.label}</p>
                    </button>
                ))}
            </div>
        </div>

        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Your Location</label>

            {/* Location search input */}
            {isLoaded ? (
                <Autocomplete
                    onLoad={(autocomplete) => { autocompleteRef.current = autocomplete; }}
                    onPlaceChanged={handlePlaceSelect}
                    options={{ componentRestrictions: { country: 'in' } }}
                >
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search for your location..."
                            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#041f96] focus:outline-none transition-colors"
                        />
                    </div>
                </Autocomplete>
            ) : (
                <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="Enter your city..."
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#041f96] focus:outline-none transition-colors"
                    />
                </div>
            )}

            {/* Use current location button */}
            <button
                type="button"
                onClick={handleUseCurrentLocation}
                className="w-full mb-3 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#041f96] hover:text-[#041f96] hover:bg-blue-50 transition-all flex items-center justify-center gap-2 text-sm"
            >
                <Navigation className="h-4 w-4" />
                Use my current location
            </button>

            {/* City & State (auto-populated or manual) */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="City *"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#041f96] focus:outline-none transition-colors"
                />
                <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    placeholder="State"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:border-[#041f96] focus:outline-none transition-colors"
                />
            </div>

            {/* Map shows when coordinates are available */}
            {formData.latitude && (
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Location Pinned
                        </span>
                        <button
                            type="button"
                            onClick={() => setShowMap(prev => !prev)}
                            className="text-xs text-[#041f96] font-medium hover:underline"
                        >
                            {showMap ? 'Hide Map' : 'Show Map'}
                        </button>
                    </div>
                    {showMap && isLoaded && (
                        <div className="rounded-xl overflow-hidden border-2 border-gray-200">
                            <div className="relative h-52 w-full">
                                <GoogleMap
                                    mapContainerStyle={{ width: '100%', height: '100%' }}
                                    center={{ lat: formData.latitude, lng: formData.longitude }}
                                    zoom={15}
                                    onClick={(e) => {
                                        const lat = e.latLng.lat();
                                        const lng = e.latLng.lng();
                                        updateField('latitude', lat);
                                        updateField('longitude', lng);
                                    }}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                    }}
                                >
                                    <Marker position={{ lat: formData.latitude, lng: formData.longitude }} />
                                </GoogleMap>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
);

export default PreferencesStep;
